import React, { useEffect, useState, useRef, useCallback } from "react";
import * as echarts from "echarts";
import axios from "axios";
import "./geomap.scss";
import "bootstrap/dist/css/bootstrap.min.css";
import { useTranslation } from "react-i18next";
import { useDatasets } from "../../hooks/useDatasets";

// Cluster label color mapping
const CLUSTER_COLORS = {
  HH: "#d73027", // High-High - Red
  LL: "#4575b4", // Low-Low - Blue
  HL: "#fdae61", // High-Low outlier - Orange
  LH: "#abd9e9", // Low-High outlier - Light Blue
  "Not Significant": "#cccccc", // Gray
};

// Get cluster color with fallback
const getClusterColor = (label) => {
  return CLUSTER_COLORS[label] || "#cccccc";
};

// European countries GeoJSON URL (Natural Earth)
const EUROPE_GEOJSON_URL =
  "https://raw.githubusercontent.com/leakyMirror/map-of-europe/master/GeoJSON/europe.geojson";

const FusionGeoMap = ({
  chartName,
  data: initialData,
  selectedRiskFactor: initialRiskFactor,
  selectedDatasetId: initialDatasetId,
  riskFactors,
  onSelectionChange,
}) => {
  const containerRef = useRef(null);
  const chartRef = useRef(null);
  const { t } = useTranslation();
  const { getAvailableDatasets, getSpecificDataset } = useDatasets();

  const [mapRegistered, setMapRegistered] = useState(false);
  const [currentRiskFactor, setCurrentRiskFactor] = useState(
    initialRiskFactor || ""
  );
  const [selectedData, setSelectedData] = useState([]);

  // Dataset switching state
  const [fusionDatasets, setFusionDatasets] = useState([]);
  const [currentDatasetId, setCurrentDatasetId] = useState(initialDatasetId || "");
  const [currentData, setCurrentData] = useState(initialData || []);
  const [datasetsLoading, setDatasetsLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(false);

  // Load all fusion category datasets on mount
  useEffect(() => {
    const loadFusionDatasets = async () => {
      setDatasetsLoading(true);
      try {
        const allDatasets = await getAvailableDatasets();
        // Filter for fusion category only
        const fusionOnly = allDatasets.filter(
          (ds) => ds.category === "fusion"
        );
        // Sort by dataset name (which typically contains the year)
        fusionOnly.sort((a, b) => a.datasetName.localeCompare(b.datasetName));
        setFusionDatasets(fusionOnly);

        // Set initial dataset if not already set
        if (!currentDatasetId && fusionOnly.length > 0) {
          // Try to find dataset matching initial data, or use last one (most recent)
          const matchingDataset = initialDatasetId
            ? fusionOnly.find(ds => ds.id === initialDatasetId)
            : fusionOnly[fusionOnly.length - 1];
          if (matchingDataset) {
            setCurrentDatasetId(matchingDataset.id);
          }
        }
      } catch (error) {
        console.error("Error loading fusion datasets:", error);
      } finally {
        setDatasetsLoading(false);
      }
    };

    loadFusionDatasets();
  }, []);

  // Load dataset data when currentDatasetId changes
  useEffect(() => {
    const loadDatasetData = async () => {
      if (!currentDatasetId) return;

      // Skip if we already have initial data and this is the initial dataset
      if (initialData?.length > 0 && currentDatasetId === initialDatasetId) {
        setCurrentData(initialData);
        return;
      }

      setDataLoading(true);
      try {
        const datasetDetails = await getSpecificDataset(currentDatasetId);
        setCurrentData(datasetDetails.elements || []);
      } catch (error) {
        console.error("Error loading dataset data:", error);
      } finally {
        setDataLoading(false);
      }
    };

    loadDatasetData();
  }, [currentDatasetId]);

  // Extract unique risk factors from current data
  const availableRiskFactors = riskFactors?.length
    ? riskFactors
    : [
        ...new Set(
          currentData?.map((item) => item["Risk Factor"]).filter(Boolean) || []
        ),
      ].map((rf) => ({ value: rf, label: rf }));

  // Set initial risk factor if not set or if current selection is invalid
  useEffect(() => {
    if (availableRiskFactors.length === 0) return;

    // Check if current risk factor exists in available options
    const currentExists = availableRiskFactors.some(
      (rf) => (rf?.value || rf) === currentRiskFactor
    );

    // Only auto-select if no current selection or current selection is invalid
    if (!currentRiskFactor || !currentExists) {
      // If initialRiskFactor is valid, use it; otherwise use first available
      const initialExists = initialRiskFactor && availableRiskFactors.some(
        (rf) => (rf?.value || rf) === initialRiskFactor
      );

      if (initialExists) {
        setCurrentRiskFactor(initialRiskFactor);
      } else {
        setCurrentRiskFactor(
          availableRiskFactors[0]?.value || availableRiskFactors[0]
        );
      }
    }
  }, [availableRiskFactors, currentRiskFactor, initialRiskFactor]);

  // Initialize ECharts once
  useEffect(() => {
    if (chartRef.current || !containerRef.current) return;

    const chart = echarts.init(containerRef.current);
    chartRef.current = chart;

    const onClick = (params) => {
      if (params?.data?.items) {
        setSelectedData(params.data.items);
      }
    };
    chart.on("click", onClick);

    const onResize = () => chart.resize();
    window.addEventListener("resize", onResize);

    return () => {
      chart.off("click", onClick);
      window.removeEventListener("resize", onResize);
      chart.dispose();
      chartRef.current = null;
    };
  }, []);

  // Load GeoJSON and register map
  useEffect(() => {
    const loadGeoJson = async () => {
      try {
        const response = await axios.get(EUROPE_GEOJSON_URL);
        const geoJson = response.data;

        if (!geoJson || !geoJson.features) {
          console.error("Invalid GeoJSON data received");
          return;
        }

        // Ensure each feature has a name property matching country names
        geoJson.features.forEach((feature) => {
          // Use NAME property if available, otherwise use name
          feature.properties.name =
            feature.properties.NAME ||
            feature.properties.name ||
            feature.properties.ADMIN;
        });

        // Unregister previous map if exists
        try {
          if (echarts.getMap("europeCountries")) {
            echarts.registerMap("europeCountries", null);
          }
        } catch (e) {
          // Map doesn't exist, continue
        }

        echarts.registerMap("europeCountries", geoJson);
        setMapRegistered(true);
      } catch (e) {
        console.error("GeoJSON fetch failed:", e);
        setMapRegistered(false);
      }
    };

    loadGeoJson();
  }, []);

  // Update chart when data or risk factor changes
  useEffect(() => {
    const chart = chartRef.current;
    if (!chart || !mapRegistered || !currentData?.length || !currentRiskFactor || dataLoading) {
      if (chart) {
        chart.clear();
      }
      return;
    }

    // Filter data by selected risk factor
    const filteredData = currentData.filter(
      (item) => item["Risk Factor"] === currentRiskFactor
    );

    // Group data by country
    const countryData = filteredData.reduce((acc, item) => {
      const country = item["Country"]?.trim();
      if (!country) return acc;
      if (!acc[country]) {
        acc[country] = [];
      }
      acc[country].push(item);
      return acc;
    }, {});

    // Map cluster labels to numeric values for visualMap
    const CLUSTER_VALUES = {
      "HH": 4,
      "HL": 3,
      "LH": 2,
      "LL": 1,
      "Not Significant": 0,
    };

    // Create series data with numeric values for visualMap
    const seriesData = Object.keys(countryData).map((country) => {
      // Get the cluster label for this country
      const items = countryData[country];
      const clusterLabel = items[0]?.["Cluster Label"] || "Not Significant";
      const numericValue = CLUSTER_VALUES[clusterLabel] ?? 0;

      return {
        name: country,
        value: numericValue,
        clusterLabel: clusterLabel,
        items: items,
      };
    });

    // Get current dataset name for title
    const currentDatasetName = fusionDatasets.find(ds => ds.id === currentDatasetId)?.datasetName || "";

    const option = {
      animation: false,
      title: {
        text: `${currentRiskFactor} - Cluster Analysis`,
        subtext: currentDatasetName,
        left: "center",
        top: 10,
        textStyle: {
          fontSize: 16,
          fontWeight: "bold",
        },
      },
      tooltip: {
        trigger: "item",
        confine: true,
        backgroundColor: "rgba(0,0,0,0.8)",
        borderColor: "transparent",
        textStyle: {
          color: "#fff",
        },
        formatter: (params) => {
          const clusterLabel = params?.data?.clusterLabel || "N/A";
          const country = params?.name || "";
          const year = params?.data?.items?.[0]?.Year || "";
          return `<strong>${country}</strong><br/>Cluster Label: <strong>${clusterLabel}</strong>${
            year ? `<br/>Year: ${year}` : ""
          }`;
        },
      },
      visualMap: {
        type: "piecewise",
        pieces: [
          { value: 4, label: "HH (High-High)", color: CLUSTER_COLORS["HH"] },
          { value: 3, label: "HL (High-Low)", color: CLUSTER_COLORS["HL"] },
          { value: 2, label: "LH (Low-High)", color: CLUSTER_COLORS["LH"] },
          { value: 1, label: "LL (Low-Low)", color: CLUSTER_COLORS["LL"] },
          { value: 0, label: "Not Significant", color: CLUSTER_COLORS["Not Significant"] },
        ],
        left: "left",
        top: "middle",
        orient: "vertical",
        textStyle: {
          color: "#333",
        },
      },
      geo: {
        map: "europeCountries",
        roam: true,
        center: [15, 54], // Center on Europe (longitude, latitude)
        zoom: 1.2,
        scaleLimit: { min: 0.5, max: 10 },
        layoutCenter: ["50%", "50%"],
        layoutSize: "100%",
        silent: false,
        itemStyle: {
          areaColor: "#f0f0f0",
          borderColor: "#999",
          borderWidth: 0.5,
        },
        emphasis: {
          itemStyle: {
            areaColor: "#e0e0e0",
            borderColor: "#666",
            borderWidth: 1,
          },
          label: {
            show: true,
            color: "#333",
          },
        },
      },
      series: [
        {
          type: "map",
          name: "Cluster Analysis",
          geoIndex: 0,
          emphasis: {
            focus: "self",
            itemStyle: {
              borderColor: "#333",
              borderWidth: 2,
            },
          },
          select: {
            itemStyle: {
              borderColor: "#000",
              borderWidth: 2,
            },
          },
          data: seriesData,
        },
      ],
    };

    chart.clear();
    chart.setOption(option, true);

    // Resize after render
    setTimeout(() => {
      if (chart && !chart.isDisposed()) {
        chart.resize();
      }
    }, 100);
  }, [mapRegistered, currentData, currentRiskFactor, currentDatasetId, fusionDatasets, dataLoading]);

  const handleRiskFactorChange = (e) => {
    const newRiskFactor = e.target.value;
    setCurrentRiskFactor(newRiskFactor);
    setSelectedData([]); // Clear selected data when changing risk factor
    // Notify parent of selection change
    if (onSelectionChange) {
      onSelectionChange(newRiskFactor, currentDatasetId);
    }
  };

  const handleDatasetChange = (e) => {
    const newDatasetId = e.target.value;
    setCurrentDatasetId(newDatasetId);
    setSelectedData([]); // Clear selected data when changing dataset
    // Notify parent of selection change
    if (onSelectionChange) {
      onSelectionChange(currentRiskFactor, newDatasetId);
    }
  };

  // Notify parent of initial selection once set
  useEffect(() => {
    if (onSelectionChange && currentRiskFactor && currentDatasetId) {
      onSelectionChange(currentRiskFactor, currentDatasetId);
    }
  }, [currentRiskFactor, currentDatasetId, onSelectionChange]);

  return (
    <>
      {/* Risk Factor and Dataset Selectors */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 16,
          marginBottom: "1rem",
          flexWrap: "wrap",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <label htmlFor="riskFactor">{t("riskFactor")}:</label>
          <select
            id="riskFactor"
            value={currentRiskFactor}
            onChange={handleRiskFactorChange}
            className="form-select"
            style={{ width: 300 }}
            disabled={dataLoading}
          >
            {availableRiskFactors.map((rf) => {
              const value = rf?.value || rf;
              const label = rf?.label || rf;
              return (
                <option key={value} value={value}>
                  {label}
                </option>
              );
            })}
          </select>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <label htmlFor="dataset">{t("dataset") || "Dataset"}:</label>
          <select
            id="dataset"
            value={currentDatasetId}
            onChange={handleDatasetChange}
            className="form-select"
            style={{ width: 320 }}
            disabled={datasetsLoading || dataLoading}
          >
            {datasetsLoading ? (
              <option value="">Loading datasets...</option>
            ) : (
              fusionDatasets.map((ds) => (
                <option key={ds.id} value={ds.id}>
                  {ds.datasetName}
                </option>
              ))
            )}
          </select>
          {dataLoading && (
            <span className="spinner-border spinner-border-sm ms-2" role="status" aria-label="Loading..."></span>
          )}
        </div>
      </div>

      {/* Map Container */}
      <div
        id={chartName}
        ref={containerRef}
        style={{
          width: "100%",
          height: "600px",
          minWidth: 300,
          touchAction: "none",
        }}
      />

      {/* Legend Explanations */}
      <div
        className="legend-explanations mt-3 p-3"
        style={{
          backgroundColor: "#f8f9fa",
          borderRadius: "8px",
          border: "1px solid #dee2e6",
        }}
      >
        <h6 className="mb-2" style={{ fontWeight: "bold" }}>
          Legend Explanation
        </h6>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: "8px", fontSize: "0.9rem" }}>
          <div style={{ display: "flex", alignItems: "flex-start", gap: "8px" }}>
            <span style={{ width: "16px", height: "16px", backgroundColor: CLUSTER_COLORS["HH"], borderRadius: "3px", flexShrink: 0, marginTop: "2px" }}></span>
            <span><strong>High-High:</strong> Countries with above-average levels of the risk factor and above-average target metric.</span>
          </div>
          <div style={{ display: "flex", alignItems: "flex-start", gap: "8px" }}>
            <span style={{ width: "16px", height: "16px", backgroundColor: CLUSTER_COLORS["HL"], borderRadius: "3px", flexShrink: 0, marginTop: "2px" }}></span>
            <span><strong>High-Low:</strong> Countries with above-average risk factor levels and below-average target metric.</span>
          </div>
          <div style={{ display: "flex", alignItems: "flex-start", gap: "8px" }}>
            <span style={{ width: "16px", height: "16px", backgroundColor: CLUSTER_COLORS["LH"], borderRadius: "3px", flexShrink: 0, marginTop: "2px" }}></span>
            <span><strong>Low-High:</strong> Countries with below-average risk factor levels and above-average target metric.</span>
          </div>
          <div style={{ display: "flex", alignItems: "flex-start", gap: "8px" }}>
            <span style={{ width: "16px", height: "16px", backgroundColor: CLUSTER_COLORS["LL"], borderRadius: "3px", flexShrink: 0, marginTop: "2px" }}></span>
            <span><strong>Low-Low:</strong> Countries with below-average risk factor levels and below-average target metric.</span>
          </div>
        </div>
      </div>

      {/* Data Table on Click */}
      {selectedData.length > 0 && (
        <div className="mt-4">
          <h5>{t("selectedRegionData") || "Selected Country Data"}</h5>
          <table className="table table-bordered table-striped">
            <thead>
              <tr>
                {Object.keys(selectedData[0]).map((k) => (
                  <th key={k}>{k}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {selectedData.map((row, i) => (
                <tr key={i}>
                  {Object.values(row).map((v, j) => (
                    <td key={j}>{v}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
};

export default FusionGeoMap;
